import NotificationsPage from './pages/NotificationsPage';
import MySupportTicketsPage from './pages/MySupportTicketsPage';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SessionTimeoutProvider } from "./context/SessionTimeoutProvider";
import { ThemeProvider } from "./context/ThemeContext";
import { BrandingProvider } from "./context/BrandingContext";
import { SavedProvider } from "./context/SavedContext";
import { MessagesProvider } from "./context/MessagesContext";
import { Header, Footer } from "./components/Layout";
import PromoBanner from "./components/PromoBanner";
import MobileNav from "./components/MobileNav";
import ScrollToTop from "./components/ScrollToTop";
import FloatingMessenger from "./components/FloatingMessenger";
import CommandPalette from "./components/CommandPalette";
import InstallPrompt from "./components/InstallPrompt";
import PushNotificationsPrompt from "./components/PushNotificationsPrompt";
import IdentityVerificationOnboardingModal from "./components/identity/IdentityVerificationOnboardingModal";
import AdminAIAssistantWidget from "./components/admin/AdminAIAssistantWidget";
import AdminAIWidgetDragController from "./components/admin/AdminAIWidgetDragController";
import SplashScreen from "./components/SplashScreen";
import AppErrorBoundary from "./components/AppErrorBoundary";
import SubscriptionGate from "./components/SubscriptionGate";
import RouteSkeleton from "./components/ui/RouteSkeleton";
import NotFoundPage from "./pages/NotFoundPage";
import { BACKEND_URL } from "./config/api";
import { applyCachedThemeColors, refreshThemeColors } from "./utils/themeColorsLoader";
import { applyCachedTypography, refreshTypography } from "./utils/typographyLoader";
import { usePageHeartbeat } from "./hooks/usePageHeartbeat";

// One-time quota recovery: older sessions may have stored 200KB+ base64
// logos under ccuk_branding which blow the localStorage quota and
// silently break every future cache write (incl. theme colours). Evict
// oversized ccuk_* entries on boot so the theme-colour save flow works
// reliably on any returning browser. Cheap enough to run every load.
(function evictOversizedCaches() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('ccuk_')) continue;
      const v = localStorage.getItem(k);
      // Anything over ~50 KB in a branding/promo/typography/theme cache
      // is almost certainly a base64 logo that belongs on the CDN.
      if (v && v.length > 50_000) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* private mode / disabled storage — ignore */
  }
})();

// Apply any admin-customised palette from localStorage on first paint
// (before any React render) — avoids FOUC on the default palette.
applyCachedThemeColors();
applyCachedTypography();

// All page-level components are lazy loaded for code-splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SuspendedPage = lazy(() => import("./pages/SuspendedPage"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const IdentityVerificationCallbackPage = lazy(() =>
  import("./pages/IdentityVerificationCallbackPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const CastingCallsPage = lazy(() => import("./pages/CastingCallsPage"));
const TalentDirectoryPage = lazy(() => import("./pages/TalentDirectoryPage"));
const FeaturedPage = lazy(() => import("./pages/FeaturedPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MyToolkitPage = lazy(() => import("./pages/MyToolkitPage"));
const AuditionRoomsPage = lazy(() => import("./pages/AuditionRoomsPage"));
const AuditionInvitationsPage = lazy(() => import("./pages/AuditionInvitationsPage"));
const TeleprompterPage = lazy(() => import("./pages/TeleprompterPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const SavedPage = lazy(() => import("./pages/SavedPage"));
const CastingCallDetailPage = lazy(() => import("./pages/CastingCallDetailPage"));
const CreateCastingCallPage = lazy(() => import("./pages/CreateCastingCallPage"));
const EditCastingCallPage = lazy(() => import("./pages/EditCastingCallPage"));
const CastingCallApplicationsPage = lazy(() => import("./pages/CastingCallApplicationsPage"));
const TalentProfileDetail = lazy(() => import("./pages/TalentProfileDetail"));
const PublicMemberProfilePage = lazy(() => import("./pages/PublicMemberProfilePage"));
const PublicProfileQrRedirectPage = lazy(() => import("./pages/PublicProfileQrRedirectPage"));
const CastingProfessionalsPage = lazy(() => import("./pages/CastingProfessionalsPage"));
const CastingProfessionalProfileDetail = lazy(() => import("./pages/CastingProfessionalProfileDetail"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceProfileDetail = lazy(() => import("./pages/ServiceProfileDetail"));
const AgentsPage = lazy(() => import("./pages/AgentsPage"));
const AgentProfileDetail = lazy(() => import("./pages/AgentProfileDetail"));
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const CompanyProfileDetail = lazy(() => import("./pages/CompanyProfileDetail"));
const CrewDirectoryPage = lazy(() => import("./pages/CrewDirectoryPage"));
const CrewProfileDetail = lazy(() => import("./pages/CrewProfileDetail"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsDetailPage = lazy(() => import("./pages/NewsDetailPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const AboutPage = lazy(() => import("./pages/StaticPages").then(m => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import("./pages/StaticPages").then(m => ({ default: m.ContactPage })));
const SupportPage = lazy(() => import("./pages/StaticPages").then(m => ({ default: m.SupportPage })));
const SupportCategoryPage = lazy(() => import("./pages/SupportCategoryPage"));
const SupportArticlePage = lazy(() => import("./pages/SupportArticlePage"));
const TermsPage = lazy(() => import("./pages/StaticPages").then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("./pages/StaticPages").then(m => ({ default: m.PrivacyPage })));
const GuidelinesPage = lazy(() => import("./pages/StaticPages").then(m => ({ default: m.GuidelinesPage })));

// Full-page loading fallback shown while initial route chunk loads
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#000000'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
      }}>
        <img
          src="/logo.png"
          alt="Casting Calls UK"
          style={{ height: '40px', opacity: 0.8 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div style={{
          width: '32px',
          height: '32px',
          border: '2px solid #002FA7',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'ccuk-spin 0.8s linear infinite'
        }} />
        <style>{`@keyframes ccuk-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

// Inline route-level fallback (skeleton, no full-screen overlay)
function RouteLoadingFallback() {
  return <RouteSkeleton />;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-theme flex items-center justify-center">
        <div className="animate-pulse text-theme-text">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Root route gate: this Emergent build is the app layer (app.castingcalls.uk).
// Public marketing lives on WordPress (castingcalls.uk), so only admins see the
// legacy in-app HomePage — everyone else lands on their dashboard, and
// logged-out visitors are sent straight to /login.
function HomeRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-theme flex items-center justify-center">
        <div className="animate-pulse text-theme-text">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = user.active_role === 'admin' || user.role === 'admin';
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <HomePage />
    </Suspense>
  );
}

// Layout with Header and Footer
//
// The Footer renders full-width on all public/marketing pages, but is hidden
// on mobile/tablet for dashboard-style app pages (dashboard, messages,
// planner, account, saved) where:
//   1. the fixed MobileNav already occupies the bottom strip, and
//   2. a long marketing footer on top of it makes the bottom feel crowded
//      and confusing.
// On lg+ the footer is always visible — preserves the existing desktop UX.
// Auth/setup pages that must remain centered & sidebar-free even when the
// user is logged in (e.g. when an admin opens /login in another tab).
// Also keep /suspended bare so a suspended user can't navigate away.
const AUTH_AND_BARE_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/suspended',
];

// Lazy-loaded app-shell side rail (Phase B). The component itself enforces
// `hidden lg:block` and a `!user` early-return, so importing it eagerly here
// is a no-op for marketing pages and signed-out visitors — but keeping it
// lazy means the bundle for logged-out users doesn't carry the sidebar code.
const DesktopLeftNav = lazy(() => import('./components/layout/DesktopLeftNav'));

function Layout({ children }) {
  const location = useLocation();
  const { user, loading: authLoading, isLoggingOut } = useAuth();
  // Send safe, throttled page-heartbeats to /api/me/heartbeat so
  // admins can see "who is on what page right now" in Admin Analytics.
  // Only mounts a fetch when the user is authenticated; strips IDs +
  // query strings + hashes; whitelist-only labels — see
  // hooks/usePageHeartbeat.js.
  usePageHeartbeat();
  // App-shell (with desktop left sidebar) applies to every logged-in user
  // on every route EXCEPT auth pages and the suspended-account screen.
  // Logged-out visitors keep the existing full-width public layout
  // regardless of pathname. The sidebar component itself enforces
  // `hidden lg:block`, so mobile/tablet are unaffected.
  const isAuthOrBare = AUTH_AND_BARE_PATHS.some(p =>
    location.pathname === p || location.pathname.startsWith(p + '/'),
  );

  // Synchronous hint: did the previous session leave a bearer token
  // behind? If so, the user is almost certainly still signed in and the
  // /api/auth/me call kicked off by AuthProvider will resolve to a real
  // user in a few hundred ms. We use this hint to render the app-shell
  // layout (sidebar + main with lg:pl-[264px]) from FIRST PAINT instead
  // of momentarily rendering the public layout and snapping ~264px to
  // the right once auth resolves. Avoids the visible page bounce on
  // hard refresh of any logged-in route.
  const hasAuthHint = typeof window !== 'undefined' && !!window.localStorage?.getItem('access_token');
  const isAppShell = !isAuthOrBare && (!!user || (authLoading && hasAuthHint) || isLoggingOut);

  // Routes that need a fixed-height "app shell" layout (no document
  // scroll, the page itself manages internal scroll panes). Messenger
  // is the only one for now — adding more here is intentional and
  // narrow. Phase 1 layout refactor: replaces the previous fragile
  // `h-[calc(100dvh - magic)]` math in MessagesPage with a clean
  // parent flex contract. Every other route keeps the old document-
  // scroll model untouched.
  const isFixedShell = isAppShell && (
    location.pathname === '/messages' || location.pathname.startsWith('/messages/')
  );

  // Mark <body> with a shell class so a single scoped CSS rule can clamp
  // shared directory page containers (max-w-screen-2xl / max-w-[1600px])
  // back to the original public website width when the visitor is NOT
  // signed in. This keeps marketing pages on a controlled max-width
  // while logged-in workspace pages keep the full wide layout.
  useEffect(() => {
    const body = document.body;
    body.classList.remove('is-public-shell', 'is-app-shell', 'is-auth-bare');
    if (isAuthOrBare) body.classList.add('is-auth-bare');
    else if (isAppShell) body.classList.add('is-app-shell');
    else body.classList.add('is-public-shell');
  }, [isAppShell, isAuthOrBare]);

  return (
    <div className={`bg-theme flex flex-col ${isFixedShell ? 'h-dvh overflow-hidden' : 'min-h-screen'}`}>
      <PromoBanner />
      <Header />

      {user
        && user.role !== 'admin'
        && user.active_role !== 'admin'
        && !isAuthOrBare
        && location.pathname !== '/settings/identity-verification'
        && (
          <IdentityVerificationOnboardingModal user={user} />
        )}

      {isAppShell ? (
        <div
          className={`ccuk-app-shell-content flex-1 pt-16 lg:pt-[72px] pb-20 lg:pb-0 overflow-x-clip ${
            isFixedShell ? 'flex flex-col min-h-0 overflow-hidden' : ''
          }`}
          data-testid="app-shell-content"
          style={{ backgroundColor: 'var(--theme-app-body, var(--theme-bg))' }}
        >
          {/* Desktop app rail — fixed to the far-left edge.
              The rail manages its own width via --ccuk-sidebar-width.
              Mobile/tablet are unaffected because DesktopLeftNav is lg+ only. */}
          <Suspense fallback={null}>
            <DesktopLeftNav />
          </Suspense>

          {/* Main app content now lives beside the fixed sidebar instead of
              wrapping the sidebar inside the centred page container.
              For fixed-shell routes (e.g. /messages), main itself becomes
              a flex column so the inner page can claim true viewport
              height via `flex-1 min-h-0` — no more `calc(100dvh - ...)`
              magic-number math drifting whenever banners appear. */}
          <main
            className={`ccuk-app-shell-main min-w-0 overflow-x-clip transition-[padding-left] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:pl-[var(--ccuk-sidebar-width,264px)] ${
              isFixedShell ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : ''
            }`}
          >
            <div
              className={
                isFixedShell
                  ? 'flex-1 min-h-0 flex flex-col overflow-hidden'
                  : 'max-w-screen-2xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-4'
              }
            >
              {children}
            </div>
          </main>
        </div>
      ) : (
        <main className="ccuk-public-shell-main flex-1 pt-16 lg:pt-[72px] pb-20 lg:pb-0">{children}</main>
      )}

      {/* Footer hidden on mobile/tablet across the whole app; shown on lg+.
          In app-shell mode, offset it beside the fixed sidebar. */}
      <div className={`hidden lg:block ${isAppShell ? 'transition-[padding-left] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:pl-[var(--ccuk-sidebar-width,264px)]' : ''}`}>
        <Footer />
      </div>

      <MobileNav />
      <FloatingMessenger />
      <InstallPrompt />
      {user && <PushNotificationsPrompt />}
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollToTop />
      <CommandPalette />
      <Routes>
        {/* Public Routes */}

        <Route path="/" element={<Layout><HomeRoute /></Layout>} />
        <Route path="/casting-calls" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><CastingCallsPage /></Suspense></Layout>} />
        <Route path="/talent" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><TalentDirectoryPage /></Suspense></Layout>} />
        <Route path="/featured" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><FeaturedPage /></Suspense></Layout>} />

        {/* Auth Routes */}
        <Route path="/login" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><LoginPage /></Suspense></Layout>} />
        <Route path="/suspended" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SuspendedPage /></Suspense></Layout>} />
        <Route path="/system-status" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><StatusPage /></Suspense></Layout>} />
        {/* Preview-only callback landing for Didit hosted verification.
            Query-params intentionally ignored — the signed webhook is
            authoritative.  No full identity UI yet. */}
        <Route path="/settings/identity-verification" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><IdentityVerificationCallbackPage /></Suspense></Layout>} />
        <Route path="/register" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><RegisterPage /></Suspense></Layout>} />

        {/* Detail pages */}
        <Route path="/casting-calls/new" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><CreateCastingCallPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/casting-calls/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><CastingCallDetailPage /></Suspense></Layout>} />
        <Route path="/casting-calls/:id/edit" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><EditCastingCallPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/casting-calls/:id/applications" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><CastingCallApplicationsPage /></Suspense></ProtectedRoute></Layout>} />
        
        <Route path="/talent/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><TalentProfileDetail /></Suspense></Layout>} />
        
        <Route path="/casting-professionals" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="casting-professionals" feature="Casting Professionals directory"><CastingProfessionalsPage /></SubscriptionGate></Suspense></Layout>} />
        <Route path="/casting-professionals/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="casting-professionals" feature="Casting Professionals directory"><CastingProfessionalProfileDetail /></SubscriptionGate></Suspense></Layout>} />
        
        <Route path="/services" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><ServicesPage /></Suspense></Layout>} />
        <Route path="/services/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><ServiceProfileDetail /></Suspense></Layout>} />
        
        <Route path="/agents" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="agents" feature="Agents directory"><AgentsPage /></SubscriptionGate></Suspense></Layout>} />
        <Route path="/agents/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="agents" feature="Agents directory"><AgentProfileDetail /></SubscriptionGate></Suspense></Layout>} />
        
        <Route path="/companies" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="companies" feature="Production Companies directory"><CompaniesPage /></SubscriptionGate></Suspense></Layout>} />
        <Route path="/companies/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="companies" feature="Production Companies directory"><CompanyProfileDetail /></SubscriptionGate></Suspense></Layout>} />
        
        <Route path="/crew" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="crew" feature="Crew directory"><CrewDirectoryPage /></SubscriptionGate></Suspense></Layout>} />
        <Route path="/crew/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SubscriptionGate directoryKey="crew" feature="Crew directory"><CrewProfileDetail /></SubscriptionGate></Suspense></Layout>} />
        
        <Route path="/events" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><EventsPage /></Suspense></Layout>} />
        <Route path="/events/:id" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><EventDetailPage /></Suspense></Layout>} />
        
        <Route path="/news" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><NewsPage /></Suspense></Layout>} />
        <Route path="/news/:slug" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><NewsDetailPage /></Suspense></Layout>} />

        {/* Marketing/Info Pages */}
        <Route path="/pricing" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><PricingPage /></Suspense></Layout>} />
        <Route path="/checkout/success" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><CheckoutSuccessPage /></Suspense></Layout>} />
        <Route path="/how-it-works" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><HowItWorksPage /></Suspense></Layout>} />

        {/* Auth Pages */}
        <Route path="/forgot-password" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><ForgotPasswordPage /></Suspense></Layout>} />
        <Route path="/reset-password" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><ResetPasswordPage /></Suspense></Layout>} />

        {/* Static Pages */}
        <Route path="/about" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><AboutPage /></Suspense></Layout>} />
        <Route path="/contact" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><ContactPage /></Suspense></Layout>} />
        <Route path="/support" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SupportPage /></Suspense></Layout>} />
        <Route path="/support/c/:categorySlug" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SupportCategoryPage /></Suspense></Layout>} />
        <Route path="/support/a/:articleSlug" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><SupportArticlePage /></Suspense></Layout>} />
        <Route path="/terms" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><TermsPage /></Suspense></Layout>} />
        <Route path="/terms-of-use" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><TermsPage /></Suspense></Layout>} />
        <Route path="/privacy" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><PrivacyPage /></Suspense></Layout>} />
        <Route path="/privacy-policy" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><PrivacyPage /></Suspense></Layout>} />
        <Route path="/cookie-policy" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><CookiePolicyPage /></Suspense></Layout>} />
        <Route path="/guidelines" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><GuidelinesPage /></Suspense></Layout>} />
        <Route path="/community-guidelines" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><GuidelinesPage /></Suspense></Layout>} />

        {/* Protected Routes */}
                <Route
  path="/notifications"
  element={
    <Layout>
      <ProtectedRoute>
        <Suspense fallback={<RouteLoadingFallback />}>
          <NotificationsPage />
        </Suspense>
      </ProtectedRoute>
    </Layout>
  }
/>
        <Route path="/dashboard" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/toolkit" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><MyToolkitPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/audition-invitations" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><AuditionInvitationsPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/audition-rooms" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><AuditionRoomsPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/teleprompter" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><TeleprompterPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/dashboard/:tab" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/saved" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><SavedPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/messages" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><MessagesPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/messages/:conversationId" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><MessagesPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/account/support" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><MySupportTicketsPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/account" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />
        {/* /account/<tab> aliases — keep deep-links like /account/settings, /account/billing, /account/profile working after refresh. */}
        <Route path="/account/:tab" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/profile/edit" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/my-listings" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />
        <Route path="/admin/*" element={<Layout><ProtectedRoute><Suspense fallback={<RouteLoadingFallback />}><DashboardPage /></Suspense></ProtectedRoute></Layout>} />

        {/* Permanent QR route comes before the single-segment member handle. */}
        <Route path="/q/:qrRef" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><PublicProfileQrRedirectPage /></Suspense></Layout>} />

        {/* Clean permanent member URL; existing static routes remain unchanged above. */}
        <Route path="/:handle" element={<Layout><Suspense fallback={<RouteLoadingFallback />}><PublicMemberProfilePage /></Suspense></Layout>} />

        {/* Fallback — friendly 404 inside the layout shell */}
        <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Refresh theme colors + typography from the backend once on mount
  // (cache already applied synchronously at module load).
  useEffect(() => {
    refreshThemeColors();
    refreshTypography();
  }, []);

  // Validate env configuration at startup
  if (!BACKEND_URL && process.env.NODE_ENV === 'development') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="max-w-md p-6 bg-white rounded-lg shadow-lg border-2 border-red-500">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Configuration Error</h1>
          <p className="text-gray-700 mb-4">
            <code className="bg-gray-100 px-2 py-1 rounded">REACT_APP_BACKEND_URL</code> is not defined.
          </p>
          <p className="text-gray-600 text-sm">
            Please check your <code className="bg-gray-100 px-1 rounded">.env</code> file and ensure 
            the backend URL is configured correctly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <BrandingProvider>
            <AuthProvider>
              <SessionTimeoutProvider>
                <SavedProvider>
                  <MessagesProvider>
                    <SplashScreen />
                    <AppRoutes />
                    <AdminAIAssistantWidget />
                    <AdminAIWidgetDragController />
                    <Toaster position="top-right" richColors />
                  </MessagesProvider>
                </SavedProvider>
              </SessionTimeoutProvider>
            </AuthProvider>
          </BrandingProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
